"use client";

import Link from "next/link";
import { ArrowRight, Sparkles, Zap, ShieldCheck, Globe, BarChart3, Clock } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import { PerceptionCore } from "./visuals";
import { hero } from "@/lib/data/homepage";

export function Hero() {
  return (
    <section id="top" className="relative overflow-hidden pt-10 pb-16 sm:pt-16 sm:pb-24">
      {/* Aurora glow */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-25 blur-[130px] animate-pulse-glow" />
        <div
          className="absolute top-40 right-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-25 blur-[140px] animate-pulse-glow"
          style={{ animationDelay: "2s" }}
        />
        <div
          className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-20 blur-[140px] animate-pulse-glow"
          style={{ animationDelay: "4s" }}
        />
      </div>

      <Container size="wide">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-6 items-center">
          {/* LEFT — copy */}
          <div className="flex flex-col items-start gap-6 text-left">
            <Reveal>
              <span className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass text-xs font-semibold uppercase tracking-[0.28em]">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                </span>
                {hero.eyebrow}
              </span>
            </Reveal>

            <Reveal delay={80}>
              <h1 className="display-hero text-balance max-w-4xl">
                {hero.titleLead}{" "}
                <span className="relative text-gradient animate-gradient-pan">
                  {hero.titleAccent}
                  <span className="pointer-events-none absolute -inset-x-3 -inset-y-2 bg-[var(--color-crimson)] opacity-50 blur-3xl -z-10" />
                </span>
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.4] font-medium text-[rgb(var(--fg))] max-w-2xl">
                {hero.lede}
              </p>
            </Reveal>

            <Reveal delay={220}>
              <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                {hero.body}
              </p>
            </Reveal>

            <Reveal delay={160}>
              <p className="text-[1.2rem] sm:text-[1.3rem] leading-[1.45] text-[rgb(var(--fg-muted))] max-w-2xl">
                {hero.lede}
              </p>
            </Reveal>

            <Reveal delay={220}>
              <p className="text-body-sm leading-relaxed text-[rgb(var(--fg-subtle))] max-w-2xl">
                {hero.body}
              </p>
            </Reveal>

            <Reveal delay={280}>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <Link
                  href={hero.primaryCta.href}
                  className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-crimson)/30]"
                >
                  {hero.primaryCta.label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  href={hero.secondaryCta.href}
                  className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                >
                  {hero.secondaryCta.label}
                </Link>
              </div>
            </Reveal>

            <Reveal delay={360}>
              <p className="text-sm font-medium text-[rgb(var(--fg-subtle))] mt-2 italic">
                {hero.trustLine}
              </p>
            </Reveal>

            <Reveal delay={420}>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-2xl">
                {[
                  { to: 412, suffix: "%", label: "Avg Penetration", color: "#dc143c", icon: Zap },
                  { to: 96.8, suffix: "%", decimals: 1, label: "Authenticity", color: "#10b981", icon: ShieldCheck },
                  { to: 50, suffix: "+", label: "Platforms", color: "#06b6d4", icon: Globe },
                  { to: 144, suffix: "D", label: "Resonance", color: "#8b5cf6", icon: BarChart3 },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="glass rounded-2xl p-3 card-glow" style={{ ["--glow" as string]: s.color }}>
                      <Icon className="h-4 w-4 mb-1" style={{ color: s.color }} />
                      <div className="display-stat-sm" style={{ color: s.color }}>
                        <Counter to={s.to} suffix={s.suffix} decimals={s.decimals ?? 0} />
                      </div>
                      <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold">
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>

          {/* RIGHT — signature visualization */}
          <Reveal delay={200} className="order-first lg:order-last">
            <div className="relative">
              <PerceptionCore />
              {/* Floating tag overlays */}
              <div className="absolute top-[6%] -left-2 sm:left-2 hidden sm:block">
                <FloatingTag
                  icon={Sparkles}
                  label="144-D Resonance"
                  color="var(--color-purple)"
                  delay="0s"
                />
              </div>
              <div className="absolute top-[20%] -right-2 sm:right-2 hidden sm:block">
                <FloatingTag
                  icon={Zap}
                  label="+412% Penetration"
                  color="var(--color-crimson)"
                  delay="1.5s"
                />
              </div>
              <div className="absolute bottom-[24%] -left-2 sm:left-2 hidden md:block">
                <FloatingTag
                  icon={Clock}
                  label="Sub-hour Deploy"
                  color="var(--color-cyan)"
                  delay="3s"
                />
              </div>
              <div className="absolute bottom-[6%] -right-2 sm:right-2 hidden md:block">
                <FloatingTag
                  icon={ShieldCheck}
                  label="99.9999% Uptime"
                  color="var(--color-emerald)"
                  delay="2s"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function FloatingTag({
  icon: Icon,
  label,
  color,
  delay,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  color: string;
  delay: string;
}) {
  return (
    <div
      className="glass rounded-full px-3 py-2 flex items-center gap-2 animate-float shadow-2xl"
      style={{ animationDelay: delay, boxShadow: `0 12px 32px -12px ${color}` }}
    >
      <Icon className="h-3.5 w-3.5" style={{ color }} />
      <span className="text-xs font-semibold">{label}</span>
    </div>
  );
}
